import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { User, Mail, KeyRound, ShieldCheck, Save, AlertCircle, CheckCircle2, Calendar } from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

export default function MyProfile() {
  const { refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Account info form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [accountSaved, setAccountSaved] = useState(false);

  // Password form
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSaved, setPwSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me`);
      setProfile(data);
      setName(data.name || "");
      setEmail(data.email || "");
    } catch (e) {
      console.error("Failed to load profile:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAccount = async (e) => {
    e.preventDefault();
    setAccountSaving(true);
    setAccountError("");
    setAccountSaved(false);
    try {
      await axios.put(`${API}/me`, { name, email });
      setAccountSaved(true);
      await refresh();      // update navbar/header user info
      await load();
      setTimeout(() => setAccountSaved(false), 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setAccountError(typeof detail === "string" ? detail : "Couldn't save changes.");
    } finally {
      setAccountSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    setPwSaved(false);
    if (newPw !== confirmPw) { setPwError("New passwords don't match."); return; }
    if (newPw.length < 8) { setPwError("New password must be at least 8 characters."); return; }
    setPwSaving(true);
    try {
      await axios.put(`${API}/me`, { current_password: currentPw, new_password: newPw });
      setPwSaved(true);
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      setTimeout(() => setPwSaved(false), 4000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setPwError(typeof detail === "string" ? detail : "Couldn't change password.");
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin" /></div>
    );
  }

  if (!profile) {
    return <div className="text-center py-24 text-[#6B7A80]">Couldn't load your profile.</div>;
  }

  return (
    <div className="max-w-2xl" data-testid="my-profile-page">
      <div className="mb-8">
        <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2">My profile</div>
        <h1 className="font-display text-4xl text-[#1F2A2E]">{profile.name}</h1>
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full ${profile.role === "admin" ? "bg-[#FCE7EB] text-[#E2718A]" : "bg-[#DDE7E9] text-[#284952]"}`}>
            {profile.role === "admin" ? "Director" : "Staff"}
          </span>
          <span className="text-xs text-[#6B7A80] flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            Joined {new Date(profile.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
          </span>
        </div>
      </div>

      {/* Account info */}
      <form onSubmit={saveAccount} className="bg-white rounded-3xl border border-[#F0E5DE] p-7 mb-6" data-testid="profile-account-form">
        <h2 className="font-display text-2xl text-[#1F2A2E] mb-1 flex items-center gap-3">
          <User className="w-5 h-5 text-[#E2718A]" /> Account info
        </h2>
        <p className="text-sm text-[#6B7A80] mb-6">Visible to other staff on referrals you receive and clients you're assigned to.</p>

        <div className="space-y-4">
          <Field label="Full name">
            <input
              className="input-navica"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="profile-name"
            />
          </Field>
          <Field label="Email address">
            <input
              type="email"
              className="input-navica"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="profile-email"
            />
            <p className="text-xs text-[#6B7A80] mt-1.5">This is also your login. Change it carefully.</p>
          </Field>
        </div>

        {accountError && (
          <div className="mt-5 p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{accountError}</span>
          </div>
        )}
        {accountSaved && (
          <div className="mt-5 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex gap-2 items-center text-sm text-emerald-800" data-testid="profile-account-saved">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> <span>Profile updated.</span>
          </div>
        )}

        <div className="flex justify-end mt-6 pt-5 border-t border-[#F0E5DE]">
          <button type="submit" disabled={accountSaving} className="btn-primary" data-testid="profile-save-account">
            {accountSaving ? "Saving..." : "Save changes"} <Save className="w-4 h-4" />
          </button>
        </div>
      </form>

      {/* Password */}
      <form onSubmit={savePassword} className="bg-white rounded-3xl border border-[#F0E5DE] p-7 mb-6" data-testid="profile-password-form">
        <h2 className="font-display text-2xl text-[#1F2A2E] mb-1 flex items-center gap-3">
          <KeyRound className="w-5 h-5 text-[#E2718A]" /> Change password
        </h2>
        <p className="text-sm text-[#6B7A80] mb-6">For your security, you'll need to enter your current password.</p>

        <div className="space-y-4">
          <Field label="Current password">
            <input
              type="password"
              autoComplete="current-password"
              className="input-navica"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
              data-testid="profile-current-password"
            />
          </Field>
          <Field label="New password">
            <input
              type="password"
              autoComplete="new-password"
              className="input-navica"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              minLength={8}
              required
              data-testid="profile-new-password"
            />
            <p className="text-xs text-[#6B7A80] mt-1.5">Min 8 characters. Use upper, lower, digit and a symbol for best security.</p>
          </Field>
          <Field label="Confirm new password">
            <input
              type="password"
              autoComplete="new-password"
              className="input-navica"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              minLength={8}
              required
              data-testid="profile-confirm-password"
            />
          </Field>
        </div>

        {pwError && (
          <div className="mt-5 p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{pwError}</span>
          </div>
        )}
        {pwSaved && (
          <div className="mt-5 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex gap-2 items-center text-sm text-emerald-800" data-testid="profile-password-saved">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> <span>Password updated.</span>
          </div>
        )}

        <div className="flex justify-end mt-6 pt-5 border-t border-[#F0E5DE]">
          <button type="submit" disabled={pwSaving || !currentPw || !newPw} className="btn-primary" data-testid="profile-save-password">
            {pwSaving ? "Updating..." : "Update password"} <Save className="w-4 h-4" />
          </button>
        </div>
      </form>

      {/* Security info */}
      <div className="bg-[#F1E4D5] rounded-3xl border border-[#F0E5DE] p-6 text-sm text-[#284952] flex gap-3 items-start">
        <ShieldCheck className="w-5 h-5 text-[#EA823A] shrink-0 mt-0.5" />
        <div>
          <strong>Your profile is private.</strong> Only the Director can see your full account details. Other staff only see your name on shared work.
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
